import { Check, ChevronsUpDown, Lock } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { useTranslation } from "../../../contexts/I18nProvider";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { cn } from "../../../lib/utils";
import { useState } from "react";
import { memo } from "react";

function ChapterSelect({ onChapterChange, chapters, defaultValue, hasPaid }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={!hasPaid}
          aria-expanded={open}
          className="w-full justify-between bg-accent/10 border-gray-200 shadow-sm disabled:cursor-not-allowed min-w-0"
        >
          {defaultValue ? (
            <div className="flex items-center gap-2 min-w-0">
              {hasPaid ? null : <Lock />}
              <span className="min-w-0 flex-1 truncate">
                {
                  chapters.find(
                    (chapter) =>
                      chapter.id === value || chapter.id === defaultValue
                  )?.name
                }
              </span>
            </div>
          ) : (
            `${t('polyglot.selectChapter')}`
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command defaultValue={defaultValue}>
          <CommandInput placeholder="Search chapter..." />
          <CommandList>
            <CommandEmpty>{t('polyglot.noChapter')}</CommandEmpty>
            <CommandGroup>
              {chapters.map((chapter) => (
                <CommandItem
                  key={chapter.id}
                  value={chapter.id}
                  onSelect={() => {
                    setValue(chapter.id);
                    onChapterChange(chapter.id);
                    setOpen(false);
                  }}
                >
                  {chapter.name}
                  <Check
                    className={cn(
                      "ml-auto",
                      value === chapter.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const areEqual = (prevProps, nextProps) => {
  // Compare defaultValue and hasPaid
  const areDefaultValueEqual =
    prevProps.defaultValue === nextProps.defaultValue;
  const areHasPaidEqual = prevProps.hasPaid === nextProps.hasPaid;

  // Compare chapters array by checking if each item is the same
  const areChaptersEqual =
    prevProps.chapters.length === nextProps.chapters.length &&
    prevProps.chapters.every(
      (chapter, index) =>
        chapter.book_id === nextProps.chapters[index].book_id &&
        chapter.chapter_id === nextProps.chapters[index].chapter_id
    );

  // Only re-render if chapters, hasPaid, or defaultValue change
  return areDefaultValueEqual && areHasPaidEqual && areChaptersEqual;
};

export default memo(ChapterSelect, areEqual);
